import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { channelLabels } from "@/lib/sectorVisibility";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";
import { Percent, Search, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const RATES = [1, 10, 20];

export default function KdvReview() {
  const { activeSectorObj } = useAuth();
  const chLabels = channelLabels(activeSectorObj);
  const [channel, setChannel] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, total_flagged: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selected, setSelected] = useState([]);
  const [rowRate, setRowRate] = useState({});
  const [bulkRate, setBulkRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const loadProg = useCallback(() => {
    api.get("/products/kdv/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, page_size: pageSize });
    if (channel) p.append("channel", channel);
    if (category) p.append("category", category);
    if (q) p.append("q", q);
    api.get(`/products/kdv/review?${p}`).then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [channel, category, q, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProg(); }, [loadProg]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats((r.data || []).map((c) => c.name))).catch(() => {});
  }, []);
  useEffect(() => { setPage(1); }, [channel, category, q]);
  useEffect(() => { setSelected([]); }, [data.items]);

  const items = data.items || [];
  const allSel = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSel ? [] : items.map((p) => p.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const verify = async (ids, vat) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/products/kdv/verify", { ids, vat_rate: vat ?? undefined });
      toast.success(`${r.updated} ürün doğrulandı${r.vat_set != null ? ` · KDV %${r.vat_set}` : ""} · kalan ${r.remaining_flagged}`);
      setSelected([]); load(); loadProg();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  // Kapsam bazlı toplu doğrulama: eşleşen TÜM bekleyen ürünler (sayfa/seçim sınırı yok), mevcut oranla.
  const verifyScope = async (scope) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/products/kdv/verify", {
        scope, confirm: true, channel: channel || undefined,
        category: scope === "category" ? category : undefined,
      });
      toast.success(`${r.updated} ürün doğrulandı · kalan ${r.remaining_flagged}`);
      setSelected([]); load(); loadProg();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 fade-in" data-testid="kdv-review-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Percent className="h-5 w-5" /></div>
          <div>
            <p className="overline">Vergi Denetimi</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">KDV Doğrulama</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700 ring-1 ring-inset ring-amber-200" data-testid="kdv-flagged-count">
            Manuel kontrol bekleyen: {data.total_flagged}
          </div>
          {data.total_flagged > 0 && (
            <Button data-testid="kdv-verify-all" size="sm" variant="outline" disabled={busy}
              onClick={() => { if (window.confirm(`${data.total_flagged} ürünün TAMAMI mevcut KDV oranlarıyla doğrulanacak. Onaylıyor musunuz?`)) verifyScope("all"); }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Tümünü Doğrula
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-500 ring-1 ring-inset ring-slate-200">
        Bu ürünler içe aktarmada "KDV & Kaynak" sayfasında <b>doğrulanması gereken</b> olarak işaretlendi (fatura / GTİP / ÜTS). Doğru oranı seçip <b>Doğrula</b> ile onaylayın; ürün listeden düşer.
      </div>

      {progress && progress.total > 0 && (
        <div className="rounded-xl bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="kdv-progress">
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">Doğrulama İlerlemesi</span>
            <span className="tabular text-slate-500" data-testid="kdv-progress-pct">
              <b className="text-emerald-600">{progress.verified}</b> / {progress.total} doğrulandı (%{progress.pct})
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress.pct}%` }} data-testid="kdv-progress-bar" />
          </div>
          {(progress.categories || []).filter((c) => c.remaining > 0).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="kdv-progress-cats">
              {progress.categories.filter((c) => c.remaining > 0).slice(0, 12).map((c) => (
                <button key={c.category} onClick={() => setCategory(c.category)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${category === c.category ? "bg-amber-500 text-white ring-amber-500" : "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100"}`}>
                  {c.category} <span className="opacity-70">· {c.remaining} kaldı</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="kdv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / SKU ara..." className="w-56 pl-9" />
        </div>
        <select data-testid="kdv-channel-filter" value={channel} onChange={(e) => setChannel(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Tüm kanallar</option>
          <option value="market">{chLabels.market}</option>
          <option value="toptan">{chLabels.toptan}</option>
        </select>
        <select data-testid="kdv-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-10 max-w-[220px] rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Tüm kategoriler</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {category && (
          <Button data-testid="kdv-verify-category" size="sm" disabled={busy}
            onClick={() => { if (window.confirm(`"${category}" kategorisindeki bekleyen tüm ürünler mevcut oranla doğrulanacak. Onaylıyor musunuz?`)) verifyScope("category"); }}
            className="bg-amber-500 text-white hover:bg-amber-600">
            "{category}" — Topluca Doğrula
          </Button>
        )}
      </div>

      {selected.length > 0 && (
        <div data-testid="kdv-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
          <span className="text-sm font-semibold text-amber-800">{selected.length} seçili</span>
          <span className="text-sm text-slate-500">→ KDV oranı:</span>
          {RATES.map((r) => (
            <button key={r} data-testid={`kdv-bulk-rate-${r}`} onClick={() => setBulkRate(String(r))}
              className={`rounded-md px-2.5 py-1 text-sm font-bold ring-1 ring-inset ${String(r) === bulkRate ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200"}`}>%{r}</button>
          ))}
          <Input data-testid="kdv-bulk-rate-input" type="number" min="0" max="100" value={bulkRate}
            onChange={(e) => setBulkRate(e.target.value)} placeholder="%" className="h-9 w-20" />
          <Button data-testid="kdv-bulk-apply" disabled={busy} onClick={() => verify(selected, bulkRate === "" ? undefined : Number(bulkRate))}
            className="bg-brand-green text-white hover:bg-brand-green-600">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            {bulkRate === "" ? "Mevcut Oranla Doğrula" : `%${bulkRate} Ata & Doğrula`}
          </Button>
        </div>
      )}

      <div className="rounded-xl bg-white ring-1 ring-inset ring-slate-200">
        {loading ? (
          <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="kdv-empty">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-3 text-sm font-medium text-slate-700">Doğrulama bekleyen ürün yok 🎉</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-400">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5"><input data-testid="kdv-select-all" type="checkbox" checked={allSel} onChange={toggleAll} /></th>
                  <th className="px-3 py-2.5 font-medium">Ürün</th>
                  <th className="px-3 py-2.5 font-medium">Kategori</th>
                  <th className="px-3 py-2.5 font-medium">Gerekçe / Kaynak</th>
                  <th className="px-3 py-2.5 text-center font-medium">Mevcut KDV</th>
                  <th className="px-3 py-2.5 text-center font-medium">Doğru Oran</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((p) => {
                  const a = p.kdv_audit || {};
                  const rate = rowRate[p.id] ?? p.vat_rate;
                  return (
                    <tr key={p.id} data-testid={`kdv-row-${p.id}`} className="hover:bg-amber-50/30">
                      <td className="px-4 py-2.5"><input data-testid={`kdv-check-${p.id}`} type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} /></td>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-slate-800">{p.name}</p>
                        <p className="font-mono text-[10px] text-slate-400">{p.sku} · {p.channel_type === "toptan" ? chLabels.toptan : chLabels.market}</p>
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</td>
                      <td className="max-w-[280px] px-3 py-2.5 text-xs text-slate-500">
                        <span className="font-semibold text-amber-700">{a.kdv_status || "—"}</span>
                        {a.kdv_reason ? <span className="block truncate">{a.kdv_reason}</span> : null}
                        {a.note ? <span className="block truncate text-slate-400">{a.note}</span> : null}
                      </td>
                      <td className="px-3 py-2.5 text-center"><span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">%{p.vat_rate}</span></td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          {RATES.map((r) => (
                            <button key={r} data-testid={`kdv-rate-${r}-${p.id}`} onClick={() => setRowRate((m) => ({ ...m, [p.id]: r }))}
                              className={`rounded px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${rate === r ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"}`}>%{r}</button>
                          ))}
                          <Input data-testid={`kdv-rate-input-${p.id}`} type="number" min="0" max="100" value={rate}
                            onChange={(e) => setRowRate((m) => ({ ...m, [p.id]: Number(e.target.value) }))} className="h-8 w-16" />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button data-testid={`kdv-verify-${p.id}`} size="sm" disabled={busy} onClick={() => verify([p.id], rate)}
                          className="bg-amber-500 text-white hover:bg-amber-600">Doğrula</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.total > pageSize && (
        <Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} />
      )}
    </div>
  );
}
